import { GridColumns } from '@mui/x-data-grid';
import {
    ERPGatewayConfigurationApi,
    GetERPGatewayConfigurationsParams,
} from '../../../services/backendServices/Apis/ERPGatewayConfigurationApi';
import {
    createAutocompleteField,
    createTextField,
    createDropdownField,
    ISearchConfiguration,
    ISearchRequest,
    ISearchResponse,
    createUseSearchService,
} from '../../../reusableFeatures';
import { paginate } from '../../../utils';
import { BuyerCompanyOption } from '../../../hooks';
import { ERPGatewayConfigurationViewModel } from '../../../services/backendServices/ViewModels';
import { subDays } from 'date-fns';
import {
    createERPGatewayFilter,
    ERPGatewayFilterParams,
} from '../../../services/backendServices/Apis/ERPGatewayConfigurationApi/ERPGatewayConfigurationsForCSP.filter';

export interface ERPGatewayConfigurationSearchParams {
    buyerCompanyId: string | null;
    gatewayName: string | null;
    dateRange: string | null;
}

// Date range options for filtering
const DATE_RANGES = {
    ALL_TIME: 'any',
    PAST_DAY: '1',
    PAST_30_DAYS: '30',
    PAST_90_DAYS: '90',
    PAST_YEAR: '365',
};

const DATE_RANGE_OPTIONS = [
    { value: DATE_RANGES.ALL_TIME, label: 'Any (including no health checks)' },
    { value: DATE_RANGES.PAST_DAY, label: 'Past 24 Hours' },
    { value: DATE_RANGES.PAST_30_DAYS, label: 'Past 30 Days' },
    { value: DATE_RANGES.PAST_90_DAYS, label: 'Past 90 Days' },
    { value: DATE_RANGES.PAST_YEAR, label: 'Past Year' },
];

export class ERPGatewayConfigurationSearchService {
    private api = new ERPGatewayConfigurationApi();

    private buyerCompanyOptions: BuyerCompanyOption[];

    constructor(buyerCompanyOptions: BuyerCompanyOption[]) {
        this.buyerCompanyOptions = buyerCompanyOptions;
    }

    async fetchSearchConfiguration(): Promise<ISearchConfiguration<ERPGatewayConfigurationSearchParams>> {
        const searchFormFieldColumns = [
            [
                createAutocompleteField({
                    label: 'Buyer Company',
                    defaultValue: null,
                    dataBindingKey: 'buyerCompanyId',
                    options: this.buyerCompanyOptions,
                }),
                createTextField({
                    label: 'Gateway Name',
                    defaultValue: '',
                    dataBindingKey: 'gatewayName',
                }),
                createDropdownField({
                    label: 'Health Check Date Range',
                    defaultValue: DATE_RANGES.ALL_TIME,
                    dataBindingKey: 'dateRange',
                    options: DATE_RANGE_OPTIONS,
                }),
            ],
        ];

        const resultGridColumns: GridColumns = [
            {
                field: 'id',
                headerName: 'ID',
                width: 100,
                sortable: false,
            },
            {
                field: 'companyName',
                headerName: 'Buyer Company',
                width: 200,
                sortable: false,
            },
            {
                field: 'gatewayName',
                headerName: 'Gateway Name',
                width: 200,
                sortable: false,
            },
            {
                field: 'lastHealthCheck',
                headerName: 'Last Health Check',
                width: 180,
                sortable: false,
            },
        ];

        return {
            gridColumns: resultGridColumns,
            searchFormFieldColumns,
            enabledExportingMethods: [],
            pageSizeOptions: [5, 10, 25],
            defaultPageSize: 10,
            searchWithNoCriteria: true,
        };
    }

    async fetchResults(
        _searchConfiguration: any,
        searchRequest: ISearchRequest<ERPGatewayConfigurationSearchParams>
    ): Promise<ISearchResponse> {
        const { buyerCompanyId, gatewayName, dateRange } = searchRequest.searchQuery.state;

        // Build filter params for the helper function
        const filterParams: ERPGatewayFilterParams = {};

        if (buyerCompanyId) {
            filterParams.companyId = parseInt(buyerCompanyId, 10);
        }

        if (dateRange && dateRange !== DATE_RANGES.ALL_TIME) {
            const daysToSubtract = parseInt(dateRange, 10);
            const pastDate = subDays(new Date(), daysToSubtract);
            filterParams.lastHealthCheckDate = pastDate;
            filterParams.onlyIncludeHealthChecks = true;
        } else {
            // default behavior is to show all results regardless of health check; only omit them if specific date range selected
            filterParams.onlyIncludeHealthChecks = false;
        }

        // Create the filter string using the helper
        const filterExpression = createERPGatewayFilter(filterParams);

        // Build complete OData query
        const params: GetERPGatewayConfigurationsParams = {
            $select: 'Id,CompanyId,LastHealthCheck,ERPName',
            $orderby: 'Id asc',
            $count: 'true',
        };

        if (filterExpression) {
            params.$filter = filterExpression;
        }

        const result = await this.api.getERPGatewayConfigurations(params);
        let filteredData = result.data.Items as ERPGatewayConfigurationViewModel[];

        // Client-side filters
        if (gatewayName && gatewayName.length > 0) {
            filteredData = filteredData.filter((config) =>
                config.ERPName.toLowerCase().includes(gatewayName.toLowerCase())
            );
        }

        const items = filteredData.map((config: ERPGatewayConfigurationViewModel) => {
            const companyName =
                this.buyerCompanyOptions.find((option) => option.value === config.CompanyID?.toString())?.label ?? '';

            let healthCheckDisplay = 'Never';

            if (config.LastHealthCheck) {
                const lastCheckDate = new Date(config.LastHealthCheck);

                healthCheckDisplay = lastCheckDate.toLocaleString();
            }
            return {
                id: config.Id!,
                companyName,
                gatewayName: config.ERPName,
                lastHealthCheck: healthCheckDisplay,
            };
        });

        return {
            pageResults: paginate(items, searchRequest.pageSize, searchRequest.pageNumber),
            totalResultCount: items.length,
        };
    }

    exportData(): Promise<void> {
        throw new Error('Method not implemented.');
    }

    get key(): string {
        return 'ERPGatewayConfiguration';
    }
}

export const useERPGatewayConfigurationSearchService = createUseSearchService(ERPGatewayConfigurationSearchService);
